import React, { FC, useCallback, useEffect, useState } from "react";

import { FieldFolder, TreeFieldMode } from "@keepeek/refront-components";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  FormControl,
  IconButton,
  MenuItem,
  Select,
  Typography,
} from "@mui/material";
import { FieldProps } from "@rjsf/utils";
import { useTranslation } from "react-i18next";

import { KIcon } from "../../../../components/common/KIcon";
import { ListFirstLevelFoldersWidget } from "./components/ListFirstLevelFoldersWidget";

export enum RootFolderMode {
  All = "all",
  Specific = "specific",
}

type ColumnConfig = {
  id: string;
  name: string;
  expanded: boolean;
};

export const CustomThematicWidget: FC<React.PropsWithChildren<FieldProps>> = function (props) {
  const { t } = useTranslation("admin");
  const { registry, onChange, formData } = props;
  const { ObjectField } = registry.fields;
  const { widgetId: WidgetIdWidget } = registry.widgets;

  // Determine le mode initial: si rootFolderId est null ou undefined, mode "All", sinon "Specific"
  const initialMode = formData?.rootFolderId == null ? RootFolderMode.All : RootFolderMode.Specific;
  const [rootFolderMode, setRootFolderMode] = useState(initialMode);
  const [previousRootFolderId, setPreviousRootFolderId] = useState(formData?.rootFolderId);

  // État pour gérer les colonnes affichées
  const [columns, setColumns] = useState<ColumnConfig[]>([
    {
      id: "kThematicWidgetColumn1",
      name: t("components.kThematicWidget.column1.label"),
      expanded: true,
    },
    {
      id: "kThematicWidgetColumn2",
      name: t("components.kThematicWidget.column2.label"),
      expanded: false,
    },
    {
      id: "kThematicWidgetColumn3",
      name: t("components.kThematicWidget.column3.label"),
      expanded: false,
    },
    {
      id: "kThematicWidgetColumn4",
      name: t("components.kThematicWidget.column4.label"),
      expanded: false,
    },
  ]);

  // Réinitialiser les sélections si le rootFolderId change
  useEffect(() => {
    if (previousRootFolderId !== formData?.rootFolderId) {
      setPreviousRootFolderId(formData?.rootFolderId);
      // Réinitialiser toutes les colonnes
      onChange({
        ...formData,
        kThematicWidgetColumn1: [],
        kThematicWidgetColumn2: [],
        kThematicWidgetColumn3: [],
        kThematicWidgetColumn4: [],
      });
    }
  }, [formData?.rootFolderId, previousRootFolderId, onChange, formData]);

  const handleColumnToggle = useCallback((columnId: string) => {
    setColumns((cols) =>
      cols.map((col) => (col.id === columnId ? { ...col, expanded: !col.expanded } : col)),
    );
  }, []);

  const handleRemoveColumn = useCallback(
    (columnId: string) => {
      setColumns((cols) => cols.filter((col) => col.id !== columnId));
      // Supprimer les données de la colonne
      const newFormData = { ...formData };
      newFormData[columnId] = [];
      onChange(newFormData);
    },
    [formData, onChange],
  );

  const handleAddColumn = useCallback(() => {
    const availableColumns = [
      { id: "kThematicWidgetColumn1", name: t("components.kThematicWidget.column1.label") },
      { id: "kThematicWidgetColumn2", name: t("components.kThematicWidget.column2.label") },
      { id: "kThematicWidgetColumn3", name: t("components.kThematicWidget.column3.label") },
      { id: "kThematicWidgetColumn4", name: t("components.kThematicWidget.column4.label") },
    ].filter((col) => !columns.find((c) => c.id === col.id));

    if (availableColumns.length > 0) {
      setColumns([...columns, { ...availableColumns[0], expanded: true }]);
    }
  }, [columns, t]);

  const renderColumn = (columnId: string) => {
    const column = columns.find((col) => col.id === columnId);
    if (!column) return null;

    const columnData = formData?.[columnId] || [];

    return (
      <Accordion
        key={column.id}
        expanded={column.expanded}
        onChange={() => handleColumnToggle(column.id)}
        sx={{ marginBottom: 2 }}
      >
        <AccordionSummary
          expandIcon={<KIcon className="fa-chevron-down" />}
          sx={{ backgroundColor: "#f5f5f5" }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              width: "100%",
              justifyContent: "space-between",
            }}
          >
            <Typography variant="h6">{column.name}</Typography>
            {columns.length > 1 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemoveColumn(column.id);
                }}
                sx={{ ml: 1 }}
              >
                <KIcon className="fa-trash" />
              </IconButton>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <Typography variant="body2" sx={{ marginBottom: 2 }}>
            {t("components.kThematicWidget.column.description")}
          </Typography>

          <Box sx={{ marginTop: 2 }}>
            {/* Liste des dossiers de premier niveau sous forme d'accordion */}
            <ListFirstLevelFoldersWidget
              columnId={columnId}
              columnData={columnData}
              formData={formData}
              onChange={onChange}
              t={t}
            />
          </Box>
        </AccordionDetails>
      </Accordion>
    );
  };

  const uiSchema = props.uiSchema;
  const uiSchemaWithHiddenFields = {
    ...uiSchema,
    id: { "ui:widget": "hidden" },
    rootFolderId: { "ui:widget": "hidden" },
    kThematicWidgetColumn1: { "ui:widget": "hidden" },
    kThematicWidgetColumn2: { "ui:widget": "hidden" },
    kThematicWidgetColumn3: { "ui:widget": "hidden" },
    kThematicWidgetColumn4: { "ui:widget": "hidden" },
  };

  return (
    <>
      {/* ID du widget */}
      <Box sx={{ marginBottom: 3 }}>
        <WidgetIdWidget
          id="id"
          name="id"
          label={t("components.kThematicWidget.id.label")}
          value={formData?.id}
          onChange={(value) => {
            onChange({
              ...formData,
              id: value,
            });
          }}
          schema={{ type: "string" }}
          uiSchema={{}}
          registry={registry}
          options={{}}
          onBlur={() => {}}
          onFocus={() => {}}
        />
      </Box>

      {/* Sélection du dossier racine */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 1, fontWeight: "bold" }}>
          {t("components.kThematicWidget.rootFolderMode.label")}
        </Typography>
        <Select
          fullWidth
          value={rootFolderMode}
          onChange={(e) => {
            const newMode = e.target.value as RootFolderMode;
            setRootFolderMode(newMode);
            if (newMode === RootFolderMode.All) {
              onChange({ ...formData, rootFolderId: null });
            }
          }}
        >
          <MenuItem value={RootFolderMode.All}>
            {t("components.kThematicWidget.rootFolderMode.all.label")}
          </MenuItem>
          <MenuItem value={RootFolderMode.Specific}>
            {t("components.kThematicWidget.rootFolderMode.specific.label")}
          </MenuItem>
        </Select>
      </Box>

      {rootFolderMode === RootFolderMode.Specific && (
        <Box sx={{ marginBottom: 3 }}>
          <FormControl fullWidth>
            <FieldFolder
              helperText={t("components.kThematicWidget.rootFolderId.description")}
              title={t("components.kThematicWidget.rootFolderId.label")}
              forceSelection={true}
              multiple={false}
              onChange={(values) => {
                onChange({
                  ...formData,
                  rootFolderId: values && values.length > 0 ? values[0] : null,
                });
              }}
              initialFolderIds={formData?.rootFolderId ? [formData.rootFolderId] : []}
              modes={[TreeFieldMode.TREE_AUTOCOMPLETE, TreeFieldMode.TREE_VIEW]}
              KIcon={KIcon}
            />
          </FormControl>
        </Box>
      )}

      {/* Sections de colonnes */}
      <Box sx={{ marginBottom: 3 }}>
        <Typography variant="subtitle1" sx={{ marginBottom: 2, fontWeight: "bold" }}>
          {t("components.kThematicWidget.columns.label")}
        </Typography>

        {columns.map((col) => renderColumn(col.id))}

        {columns.length < 4 && (
          <Button
            variant="contained"
            startIcon={<KIcon className="fa-plus" />}
            onClick={handleAddColumn}
            fullWidth
            sx={{ marginTop: 2 }}
          >
            {t("components.kThematicWidget.addColumn.label")}
          </Button>
        )}
      </Box>

      {/* Autres champs (couleurs, etc.) */}
      <ObjectField {...props} uiSchema={uiSchemaWithHiddenFields} />
    </>
  );
};
